import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, ArrowRight, Check } from "lucide-react";
import { CONTACT } from "../mock";
import { useToast } from "../hooks/use-toast";
import { Script } from "./Script";

const BUDGETS = ["do 25 000 Kč", "25–50 000 Kč", "50–100 000 Kč", "100 000+ Kč"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", budget: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const messageRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail || "";
      setForm((f) => ({ ...f, message: msg }));
      setSent(false);
      setTimeout(() => messageRef.current && messageRef.current.focus({ preventScroll: true }), 700);
    };
    window.addEventListener("prefill-contact", handler);
    return () => window.removeEventListener("prefill-contact", handler);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Vyplňte prosím všechna pole", description: "Jméno, e-mail a zpráva jsou povinné." });
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/leads`, form);
      setSent(true);
      toast({ title: "Odesláno!", description: "Ozveme se vám do 24 hodin." });
      setForm({ name: "", email: "", budget: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      toast({ title: "Něco se nepovedlo", description: "Zkuste to prosím znovu za chvíli." });
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full bg-[#141412] border border-white/10 rounded-2xl px-4 py-3.5 text-white placeholder:text-[#6b6b64] outline-none focus:border-[#d8ff00]/60 transition-colors";

  return (
    <section id="kontakt" className="bg-[#0e0e0d] py-24 md:py-32" data-testid="contact-section">
      <div className="container-x grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div className="reveal">
          <p className="eyebrow text-[#8b8b80]">Kontakt</p>
          <h2 className="display text-white text-4xl sm:text-5xl lg:text-6xl mt-4">
            Pojďme do toho{" "}
            <Script color="#d8ff00" className="text-[#d8ff00] text-5xl sm:text-6xl lg:text-7xl mx-2">
              spolu.
            </Script>
          </h2>
          <p className="text-[#a5a59d] mt-10 max-w-md leading-relaxed">
            Popište nám svůj záměr. Ozveme se s návrhem řešení a orientační nabídkou — zcela nezávazně.
          </p>

          <div className="mt-12 space-y-4">
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
              <span className="grid place-items-center w-11 h-11 rounded-full border border-white/12 text-[#d8ff00] transition-all group-hover:bg-[#d8ff00] group-hover:text-[#0e0e0d]">
                <Mail size={18} />
              </span>
              <span className="text-[#e3e3da] group-hover:text-white transition-colors">{CONTACT.email}</span>
            </a>
            <a
              href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
              data-testid="contact-phone"
              className="flex items-center gap-4 group"
            >
              <span className="grid place-items-center w-11 h-11 rounded-full border border-white/12 text-[#d8ff00] transition-all group-hover:bg-[#d8ff00] group-hover:text-[#0e0e0d]">
                <Phone size={18} />
              </span>
              <span className="text-[#e3e3da] group-hover:text-white transition-colors">{CONTACT.phone}</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="grid place-items-center w-11 h-11 rounded-full border border-white/12 text-[#d8ff00]">
                <MapPin size={18} />
              </span>
              <span className="text-[#e3e3da]">{CONTACT.location}</span>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="reveal bg-[#171714] border border-white/10 rounded-[28px] p-7 md:p-9 space-y-5"
        >
          <div>
            <label className="block text-sm text-[#a5a59d] mb-2">Jméno</label>
            <input value={form.name} onChange={set("name")} placeholder="Jan Novák" className={field} data-testid="contact-input-name" />
          </div>
          <div>
            <label className="block text-sm text-[#a5a59d] mb-2">E-mail</label>
            <input
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="jan@firma.cz"
              className={field}
              data-testid="contact-input-email"
            />
          </div>
          <div>
            <label className="block text-sm text-[#a5a59d] mb-2">Orientační rozpočet</label>
            <div className="flex flex-wrap gap-2">
              {BUDGETS.map((b) => (
                <button
                  type="button"
                  key={b}
                  onClick={() => setForm((f) => ({ ...f, budget: b }))}
                  data-testid={`contact-budget-${BUDGETS.indexOf(b)}`}
                  className={`px-3.5 py-2 rounded-full text-sm border transition-all ${
                    form.budget === b
                      ? "bg-[#d8ff00] text-[#0e0e0d] border-[#d8ff00]"
                      : "border-white/12 text-[#c9c9bf] hover:border-white/35"
                  }`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm text-[#a5a59d] mb-2">Zpráva</label>
            <textarea
              ref={messageRef}
              value={form.message}
              onChange={set("message")}
              rows={4}
              placeholder="Potřebuji nový web pro..."
              className={`${field} resize-none`}
              data-testid="contact-input-message"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className={`w-full py-4 rounded-full font-semibold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-70 ${
              sent ? "bg-[#0e0e0d] text-[#d8ff00] border border-[#d8ff00]" : "btn-lime"
            }`}
          >
            {sent ? (
              <>
                Odesláno <Check size={18} />
              </>
            ) : loading ? (
              <>Odesílám…</>
            ) : (
              <>
                Odeslat poptávku <ArrowRight size={18} />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
